// Returns a ref and corresponding helpers to store location cards heights for future reference.
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { theme } from '@sweetgreen/garnish';

import type { LocationResultData } from '../../GraphQL';

// ────────────────────────────────────────────────────────────────────────────────

export const useLocationCardsHeights = (
  props: UseLocationCardsHeightsProps,
) => {
  const {
    searchString,
    locations,
    locationSearchType,
    startsWithDividerTitle,
  } = props;

  // ─────────────────────────────────────────────────────────────────

  const firstLocationId = locations?.[0]?.id;
  const [firstLocationCardHeight, setFirstLocationCardHeight] = useState(0);

  // ─────────────────────────────────────────────────────────────────

  const locationCardsHeights = useRef(new Map<string, number>());

  // ─── HELPERS ─────────────────────────────────────────────────────

  const storeLocationCardHeight = useCallback(
    (event: LayoutChangeEvent, locationId: string) => {
      const { height } = event.nativeEvent.layout;
      const isFirstLocation = locationId === firstLocationId;
      const titleOffset = theme.spacing.xlarge + theme.spacing.medium;
      const firstOffset = startsWithDividerTitle ? titleOffset : 0;

      if (isFirstLocation) setFirstLocationCardHeight(height + firstOffset);
      locationCardsHeights.current.set(locationId, height);
    },
    [firstLocationId, startsWithDividerTitle],
  );

  // ─── EFFECTS ─────────────────────────────────────────────────────

  // reset first card height on locations or locations search type change
  useLayoutEffect(() => {
    setFirstLocationCardHeight(0);
  }, [locationSearchType, locations]);

  // reset cards heights Map on search string change
  useEffect(() => {
    const scrollPositionsMap = locationCardsHeights.current;

    return () => {
      scrollPositionsMap.clear();
    };
  }, [searchString]);

  // ────────────────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      locationCardsHeights,
      storeLocationCardHeight,
      firstLocationCardHeight,
    }),
    [firstLocationCardHeight, storeLocationCardHeight],
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type UseLocationCardsHeightsProps = {
  readonly searchString: string;
  readonly locations: readonly LocationResultData[];
  readonly locationSearchType: string;
  readonly startsWithDividerTitle?: boolean;
};
